import React, { useEffect, useState } from 'react';
import type { SchoolReportCsvJobStatusResponse } from '@whizz/api-model';
import type { ToggleModalProps } from '../../components/modal/types';
import type { EndDateType, StartDateType } from '../../util/types';
import { model } from '@whizz/api-client';
import { useI18n } from '@whizz/react-i18n';
import { Button, Loader } from '../../components';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../util/vars';
import { generatePDF } from '../../helper/pdfHelper';
import { saveAsCsv } from '../../util/reactFunc';
import { DateTime } from 'luxon';
import { getDateTimeObj } from '../../util/dates';
import { useQuery } from 'react-query';

export type SchoolDownloadModalProps = Pick<ToggleModalProps, 'toggle'> & { startDate: StartDateType, endDate: EndDateType };

const SchoolDownloadModal = ({ toggle, startDate, endDate }: SchoolDownloadModalProps): JSX.Element => {

  const { locale, translate } = useI18n();
  const [ isFileDownloading, setIsFileDownloading ] = useState<'pdf' | 'csv' | false>(false);
  const [ studentsDownloadEnabled, setStudentsDownloadEnabled ] = useState(false);
  const [ classesDownloadEnabled, setClassesDownloadEnabled ] = useState(false);

  const elementsIds =
     [
       'settings-container',
       'assessment-info-container', // TBD remove if unnecessary
       'table-container',
       'maths-age-delta-container',
       'school-progressions-container',
       'school-quality-learning-time-container',
       'school-maths-age-improvements-container',
       'school-progress-consistency-container',
       'school-student-status-container',
       'initial-assessment-completed-container',
       'school-topic-progress-container',
     ];

  const studentsJobQuery = useQuery(
    [ QUERIES_IDS.CSV_REPORT_STUDENTS_JOB, startDate, endDate, locale?.id ],
    () => model.CsvReports.generateStudentsJob(locale, startDate, endDate ?? DateTime.now()),
    { enabled: studentsDownloadEnabled, cacheTime: 0 },
  );

  const studentsStatusQuery = useQuery<SchoolReportCsvJobStatusResponse>(
    [ QUERIES_IDS.CSV_REPORT_STUDENTS, studentsJobQuery.data?.jobId ],
    () => model.CsvReports.getStudentsJobStatus(studentsJobQuery.data!.jobId),
    {
      enabled: studentsJobQuery.data?.jobId != null,
      refetchInterval: (data) => (data?.status === 'complete' || data?.status === 'error' ? false : 2000),
      cacheTime: 0,
    },
  );

  const classesJobQuery = useQuery(
    [ QUERIES_IDS.CSV_REPORT_CLASSES_JOB, startDate, endDate, locale?.id ],
    () => model.CsvReports.generateClassesJob(locale, startDate, endDate ?? DateTime.now()),
    { enabled: classesDownloadEnabled, cacheTime: 0 },
  );

  const classesStatusQuery = useQuery<SchoolReportCsvJobStatusResponse>(
    [ QUERIES_IDS.CSV_REPORT_CLASSES, classesJobQuery.data?.jobId ],
    () => model.CsvReports.getClassesJobStatus(classesJobQuery.data!.jobId),
    {
      enabled: classesJobQuery.data?.jobId != null,
      refetchInterval: (data) => (data?.status === 'complete' || data?.status === 'error' ? false : 2000),
      cacheTime: 0,
    },
  );

  useEffect(() => {
    if (!studentsDownloadEnabled) return;
    if (studentsStatusQuery.data?.status !== 'complete' || !studentsStatusQuery.data.csv) return;
    saveAsCsv(
      studentsStatusQuery.data.csv,
      translate('pages.REPORTS.DOWNLOAD.FILE_NAMES.student', { date: getDateTimeObj(DateTime.now(), locale?.id).toFormat('yyyy_LL_dd') }).toUpperCase(),
    );
    setIsFileDownloading(false);
    toggle(false);
  }, [ studentsStatusQuery.data?.status, studentsDownloadEnabled ]);

  useEffect(() => {
    if (!classesDownloadEnabled) return;
    if (classesStatusQuery.data?.status !== 'complete' || !classesStatusQuery.data.csv) return;
    saveAsCsv(
      classesStatusQuery.data.csv,
      translate('pages.REPORTS.DOWNLOAD.FILE_NAMES.school', { date: getDateTimeObj(DateTime.now(), locale?.id).toFormat('yyyy_LL_dd') }).toUpperCase(),
    );
    setIsFileDownloading(false);
    toggle(false);
  }, [ classesStatusQuery.data?.status, classesDownloadEnabled ]);

  const togglePrinting = (print: boolean) => {
    const tag = document.getElementsByTagName('html')[0];
    if (print) {
      tag?.classList.add('printing');
    } else {
      tag?.classList.remove('printing');
    }
  };

  const handleCurrentView = () => {
    togglePrinting(true);
    setIsFileDownloading('pdf');
    const styleLinks: NodeListOf<HTMLLinkElement> = document.querySelectorAll('link[href^=\'https://wchat\']') ;
    const chatButton = document.getElementById('fc_frame');
    const chatFrame = document.getElementById('fc_push_frame');
    if (null != chatButton) chatButton.style.display = 'none';
    if (null != chatFrame) chatFrame.style.display = 'none'; // Prevents default div styles to be applied while generating the PDF
    styleLinks.forEach((stylesheet) => stylesheet.disabled = true);
    setTimeout(() => {
      generatePDF('report', elementsIds).then(() => {
        togglePrinting(false);
        setIsFileDownloading(false);
        toggle(false);
      });
    }, 250);
  };

  const handleStudentData = () => {
    setIsFileDownloading('csv');
    setStudentsDownloadEnabled(true);
  };

  const handleClassData = () => {
    setIsFileDownloading('csv');
    setClassesDownloadEnabled(true);
  };

  if (isFileDownloading) {
    return (
      <Loader
        label={translate(`pages.REPORTS.DOWNLOAD.MODAL.loader_${isFileDownloading}`)}
      />
    );
  }

  return (
    <>
      <div className={'flex items-center justify-center container'}>
        <Button
          icon={GLOBAL_ICONS.download}
          onClick={handleCurrentView}
          isOutline
          color={'positive'}
          className={'mr-md'}
        >
          {translate('components.BUTTONS.DOWNLOAD.current')}
        </Button>
        <Button
          icon={GLOBAL_ICONS.download}
          onClick={handleClassData}
          isOutline
          color={'positive'}
          className={'mr-md'}
        >
          {translate('components.BUTTONS.DOWNLOAD.class')}
        </Button>
        <Button
          icon={GLOBAL_ICONS.download}
          onClick={handleStudentData}
          isOutline
          color={'positive'}
          className={'mr-md'}
        >
          {translate('components.BUTTONS.DOWNLOAD.all')}
        </Button>
      </div>
    </>
  );
};

SchoolDownloadModal.displayName = 'SchoolDownloadModal';

export default SchoolDownloadModal;
